import React, { useState, useEffect } from 'react';
import SandhiAnalysisSlp1 from './SandhiAnalysisSlp1';
import { SandhiRightSidebar } from './SandhiAnalysisSidebar';
import { IdentificationRightSidebar } from './IdentificationSidebar';
import HelpPDFsModal from './HelpPDFsModal';
import Identification from './Identification';
import './App.css';

// If you're using CRA's `proxy` (package.json), leave REACT_APP_API_URL unset
// and requests will go to the dev server origin (and be proxied to Flask).
// If you deploy frontend+backend separately, set REACT_APP_API_URL to your Flask base URL.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function splitWords(text) {
  return String(text ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/\.+$/, ''));
}

/** Sandhi tab: DB `status` `complete` → green row highlight */
function isSandhiComplete(sentence) {
  return String(sentence?.status ?? '').trim().toLowerCase() === 'complete';
}

/** Morphological tab: independent `morph_status` → green row highlight */
function isMorphComplete(sentence) {
  return String(sentence?.morph_status ?? '').trim().toLowerCase() === 'complete';
}

function identificationRowsForEdit(xmlid, text, storedByXmlid) {
  const stored = storedByXmlid[xmlid] || [];
  return splitWords(text).map((_, i) =>
    stored[i]
      ? { ...stored[i] }
      : { inflection: '', lemma: '', lexical_category: '' }
  );
}

/** Map GET /morphology `words` payload to per-word identification rows. */
function morphologyWordsToRows(words, wordCount) {
  const byIndex = {};
  for (const w of words || []) {
    const wxmlid = String(w.wxmlid ?? '');
    const m = wxmlid.match(/\.w(\d+)$/);
    if (!m) continue;
    const i = Number(m[1]) - 1;
    if (i < 0) continue;
    byIndex[i] = {
      inflection: w.inflection ?? '',
      lemma: w.lemma ?? '',
      lexical_category: w.lexid ?? w.lexical_category ?? '',
    };
  }
  return Array.from({ length: wordCount }, (_, i) =>
    byIndex[i]
      ? { ...byIndex[i] }
      : { inflection: '', lemma: '', lexical_category: '' }
  );
}

function App() {
  // sentences: [{ xmlid, text, original_text, ana, flagged }]
  const [sentences, setSentences] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // analysis from check endpoint, keyed by xmlid: { show, text: { slp1Units } }
  const [analysisByXmlid, setAnalysisByXmlid] = useState({});
  const [helpPdfModalShow, setHelpPdfModalShow] = useState(false);
  const [helpPdfModalPdf, setHelpPdfModalPdf] = useState(null);
  const [activeInterface, setActiveInterface] = useState('sandhi');
  const [morphSelectedIndex, setMorphSelectedIndex] = useState(null);
  /** For status=complete sentences in edit mode: one { inflection, lemma, lexical_category } per word */
  const [completeLexicalByXmlid, setCompleteLexicalByXmlid] = useState({});

  useEffect(() => {
    fetchSentences();
  }, []);

  const fetchSentences = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/sentences`);
      if (!res.ok) throw new Error('Failed to fetch sentences');
      const data = await res.json();
      const list = Array.isArray(data.sentences) ? data.sentences : [];
      setSentences(
        list.map((s) => ({
          ...s,
          flagged: s.flagged === true,
          status:
            s.status != null && String(s.status).trim() !== ''
              ? String(s.status).trim()
              : 'inProgress',
          morph_status:
            s.morph_status != null && String(s.morph_status).trim() !== ''
              ? String(s.morph_status).trim()
              : 'inProgress',
        }))
      );
      await loadAllMorphology(list);
    } catch (err) {
      console.error('Error fetching sentences:', err);
      setError(err.message);
      setSentences([]);
    } finally {
      setLoading(false);
    }
  };

  const persistSentence = async (xmlid, text, status) => {
    const body = { text };
    if (status !== undefined) body.status = status;
    const res = await fetch(
      `${API_BASE_URL}/api/sentences/${encodeURIComponent(xmlid)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Failed to save sentence');
    }
  };

  /** Call backend to run sandhi analysis; returns and stores analysis for display. */
  const submitForCheck = async (xmlid, text, questionText) => {
    const res = await fetch(`${API_BASE_URL}/api/sentences/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        xmlid,
        text,
        questionText: questionText ?? text,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.message || 'Check request failed');
    }
    if (data.analysis) {
      setAnalysisByXmlid((prev) => ({ ...prev, [xmlid]: data.analysis }));
      // Update sentence status in state so complete green highlight shows immediately
      if (data.analysis.status) {
        setSentences((prev) =>
          prev.map((s) =>
            s.xmlid === xmlid ? { ...s, status: data.analysis.status } : s
          )
        );
      }
    }
  };

  const fetchMorphologyForSentence = async (xmlid, text) => {
    const res = await fetch(
      `${API_BASE_URL}/api/sentences/${encodeURIComponent(xmlid)}/morphology`
    );
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    return morphologyWordsToRows(data.words, splitWords(text).length);
  };

  const loadAllMorphology = async (sentenceList) => {
    const entries = await Promise.all(
      sentenceList.map(async (s) => {
        try {
          const rows = await fetchMorphologyForSentence(s.xmlid, s.text);
          return rows ? [s.xmlid, rows] : null;
        } catch (err) {
          console.error(`Error loading morphology for ${s.xmlid}:`, err);
          return null;
        }
      })
    );
    const next = {};
    for (const entry of entries) {
      if (entry) next[entry[0]] = entry[1];
    }
    setCompleteLexicalByXmlid(next);
  };

  /** Save per-word morphology (inflection/lemma/lexical_category) for a sentence. */
  const persistMorphology = async (xmlid, rows, words) => {
    const payload = {
      words: rows.map((row, i) => ({
        wxmlid: `${xmlid}.w${i + 1}`,
        word: words?.[i] ?? '',
        lemma: row?.lemma ?? '',
        inflection: row?.inflection ?? '',
        lexical_category: row?.lexical_category ?? '',
      })),
    };
    const res = await fetch(
      `${API_BASE_URL}/api/sentences/${encodeURIComponent(xmlid)}/morphology`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Failed to save morphology');
    }
    const data = await res.json().catch(() => ({}));
    setCompleteLexicalByXmlid((prev) => ({
      ...prev,
      [xmlid]: rows.map((row) => ({ ...row })),
    }));
    if (data.morph_status) {
      setSentences((prev) =>
        prev.map((s) =>
          s.xmlid === xmlid ? { ...s, morph_status: data.morph_status } : s
        )
      );
    }
    return data;
  };

  const persistFlag = async (xmlid, flagged) => {
    const res = await fetch(
      `${API_BASE_URL}/api/sentences/${encodeURIComponent(xmlid)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flagged }),
      }
    );
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Failed to update flag');
    }
  };

  /** Resize per-word lexical fields to match whitespace-split word count (complete sentences only). */
  const syncLexicalRowsForXmlid = (xmlid, text) => {
    const words = splitWords(text);
    const n = words.length;
    setCompleteLexicalByXmlid((prev) => {
      const cur = prev[xmlid] || [];
      const next = Array.from({ length: n }, (_, i) =>
        cur[i]
          ? { ...cur[i] }
          : { inflection: '', lemma: '', lexical_category: '' }
      );
      return { ...prev, [xmlid]: next };
    });
  };

  const updateCompleteLexicalField = (xmlid, wordIndex, field, value) => {
    setCompleteLexicalByXmlid((prev) => {
      const rows = [...(prev[xmlid] || [])];
      if (!rows[wordIndex]) {
        rows[wordIndex] = { inflection: '', lemma: '', lexical_category: '' };
      }
      rows[wordIndex] = { ...rows[wordIndex], [field]: value };
      return { ...prev, [xmlid]: rows };
    });
  };

  const handleLineClick = (index, e) => {
    // Don't let this click bubble up to the container "click anywhere" handler
    if (e) {
      e.stopPropagation();
    }

    // If clicking a different line while one is being edited, switch edit target
    const s = sentences[index];
    setEditingIndex(index);
    setEditValue(s.text);
    if (isSandhiComplete(s)) {
      syncLexicalRowsForXmlid(s.xmlid, s.text);
    }
  };

  const handleMorphLineClick = async (index, e) => {
    if (e) e.stopPropagation();
    const s = sentences[index];
    if (!s) return;
    if (morphSelectedIndex === index) {
      setMorphSelectedIndex(null);
      return;
    }

    try {
      const rows = await fetchMorphologyForSentence(s.xmlid, s.text);
      if (rows) {
        setCompleteLexicalByXmlid((prev) => ({ ...prev, [s.xmlid]: rows }));
      } else {
        syncLexicalRowsForXmlid(s.xmlid, s.text);
      }
    } catch (err) {
      console.error('Error loading morphology:', err);
      syncLexicalRowsForXmlid(s.xmlid, s.text);
    }
    setMorphSelectedIndex(index);
  };

  const handleSave = async (index) => {
    const current = sentences[index];
    const hasChange = editValue !== current.text;

    if (hasChange) {
      const before = sentences;
      const updated = [...sentences];
      updated[index] = { ...current, text: editValue };
      setSentences(updated);
      setEditingIndex(null);
      setEditValue('');

      let saveOk = false;
      try {
        await persistSentence(current.xmlid, editValue);
        saveOk = true;
        // Reload to keep derived Devanagari fields in sync after edits.
        await fetchSentences();
      } catch (err) {
        console.error('Error saving:', err);
        setError(err.message || 'Failed to save sentence');
        setSentences(before);
      }

      try {
        await submitForCheck(
          current.xmlid,
          editValue,
          current.original_text
        );
      } catch (err) {
        console.error('Error running check:', err);
        if (saveOk) setError(err.message || 'Save succeeded but check failed');
      }
    } else {
      setEditingIndex(null);
      setEditValue('');
      try {
        await submitForCheck(
          current.xmlid,
          editValue,
          current.original_text
        );
      } catch (err) {
        console.error('Error running check:', err);
        setError(err.message || 'Check failed');
      }
    }
  };

  const handleCancel = () => {
    // Cancel edit, keep current text
    setEditingIndex(null);
    setEditValue('');
  };

  // Clicking on the background / list container exits edit mode
  const handleContainerClick = () => {
    if (editingIndex !== null) {
      handleCancel();
    }
    if (activeInterface === 'morphological' && morphSelectedIndex !== null) {
      setMorphSelectedIndex(null);
    }
  };

  // Revert button (in view mode) – undo to original sentence and always set status to inProgress
  const handleUndo = async (index, e) => {
    e.stopPropagation();
    const current = sentences[index];
    const previousText = current.text;
    const previousStatus = current.status;
    const revertedText = current.original_text ?? current.text;
    const textChanged = previousText !== revertedText;

    // Optimistically update UI: revert text (if needed), clear green highlight, remove analysis
    setSentences((prev) =>
      prev.map((s, i) =>
        i === index
          ? {
              ...s,
              text: textChanged ? revertedText : s.text,
              status: 'inProgress',
            }
          : s
      )
    );
    setAnalysisByXmlid((prev) => {
      const { [current.xmlid]: _, ...rest } = prev;
      return rest;
    });

    try {
      await persistSentence(current.xmlid, revertedText, 'inProgress');
    } catch (err) {
      console.error('Error reverting:', err);
      setError(err.message || 'Failed to revert sentence');

      // On failure, restore previous text and status
      setSentences((curr) =>
        curr.map((s, i) =>
          i === index
            ? {
                ...s,
                text: textChanged ? previousText : s.text,
                status: previousStatus,
              }
            : s
        )
      );
    }
  };

  const handleFlag = async (index, e) => {
    e.stopPropagation();
    const current = sentences[index];
    const newFlagged = !(current.flagged === true);

    const updated = [...sentences];
    updated[index] = { ...current, flagged: newFlagged };
    setSentences(updated);

    try {
      await persistFlag(current.xmlid, newFlagged);
    } catch (err) {
      console.error('Error updating flag:', err);
      setError(err.message || 'Failed to update flag');
      setSentences((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], flagged: current.flagged };
        return copy;
      });
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSave(index);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      handleCancel();
    }
  };

  const openHelpPdf = (pdf) => {
    setHelpPdfModalPdf(pdf || null);
    setHelpPdfModalShow(true);
  };

  const switchInterface = (target) => {
    setActiveInterface(target);
    setEditingIndex(null);
    setEditValue('');
    setMorphSelectedIndex(null);
  };

  return (
    <div className="App">
      <div className="app-two-col">
        <div className="app-main">
          <div className="container">
            <h1>Annotation</h1>
            <div className="analysis-nav">
              <button
                type="button"
                className={`analysis-nav-button ${
                  activeInterface === 'sandhi' ? 'active' : ''
                }`}
                onClick={() => switchInterface('sandhi')}
              >
                Sandhi analysis
              </button>
              <button
                type="button"
                className={`analysis-nav-button ${
                  activeInterface === 'morphological' ? 'active' : ''
                }`}
                onClick={() => switchInterface('morphological')}
              >
                Morphological analysis
              </button>
            </div>
            <HelpPDFsModal
              show={helpPdfModalShow}
              onHide={() => { setHelpPdfModalShow(false); setHelpPdfModalPdf(null); }}
              pdf={helpPdfModalPdf}
            />

            {loading && <div style={{ marginBottom: 12 }}>Loading…</div>}
            {error && (
              <div style={{ marginBottom: 12, color: '#b00020' }}>
                {error}{' '}
                <button onClick={fetchSentences} style={{ marginLeft: 8 }}>
                  Retry
                </button>
              </div>
            )}

            <div className="list-container" onClick={handleContainerClick}>
              {sentences.map((sentence, index) => {
            const isEditing = editingIndex === index;
            const isFlagged = sentence.flagged === true;
            const isModified =
              activeInterface === 'sandhi' &&
              sentence.text !== sentence.original_text;
            const analysis = analysisByXmlid[sentence.xmlid];
            const isSandhiDone = isSandhiComplete(sentence);
            const isMorphDone = isMorphComplete(sentence);
            const isMorphOpen =
              activeInterface === 'morphological' && morphSelectedIndex === index;
            const showComplete =
              activeInterface === 'sandhi'
                ? isSandhiDone && !isEditing
                : isMorphDone && !isMorphOpen;

            const lineClasses = [
              'sentence-line',
              isEditing ? 'editing' : '',
              isFlagged ? 'flagged' : '',
              isModified ? 'edited' : '',
              showComplete ? 'complete' : '',
              isMorphOpen ? 'morph-open' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div
                key={sentence.xmlid || index}
                className={lineClasses}
                onClick={(e) =>
                  activeInterface === 'sandhi'
                    ? handleLineClick(index, e)
                    : handleMorphLineClick(index, e)
                }
              >
                {activeInterface === 'sandhi' && isEditing ? (
                  <div className="edit-mode">
                    <div className="edit-mode-row">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditValue(v);
                          if (isSandhiComplete(sentence)) {
                            syncLexicalRowsForXmlid(sentence.xmlid, v);
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        autoFocus
                      />
                      <div className="edit-actions">
                        {/* 1) Check / submit changes – runs save then backend /api/sentences/check */}
                        <button
                          type="button"
                          className="save-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSave(index);
                          }}
                          title="Check (save changes)"
                        >
                          ✓
                        </button>
                        {/* Cancel edit */}
                        <button
                          className="cancel-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancel();
                          }}
                          title="Cancel edit"
                        >
                          ✕
                        </button>
                        {/* 3) Flag button */}
                        <button
                          type="button"
                          className={`flag-button ${
                            isFlagged ? 'flagged' : ''
                          }`}
                          onClick={(e) => handleFlag(index, e)}
                          title="Flag sentence"
                        >
                          ⚑
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="sentence-content">
                    {/* Column 1: XMLID */}
                    <div className="sentence-xmlid">{sentence.xmlid}</div>
                    {/* Column 2: sentence text with analysis under it */}
                    <div className="sentence-text-column">
                      {activeInterface === 'morphological' ? (
                        <div className="morph-text-block">
                          <div className="sentence-text deva-text">
                            {sentence.deva_original_text || sentence.original_text}
                          </div>
                          <div className="sentence-text deva-text">
                            {sentence.deva_text || sentence.text}
                          </div>
                        </div>
                      ) : (
                        <div className="sentence-text">{sentence.text}</div>
                      )}
                      {activeInterface === 'sandhi' && analysis?.show && (
                        <>
                          <div className="original-question">
                            {sentence.original_text}
                          </div>
                          <div className="analysis">
                            <SandhiAnalysisSlp1
                              slp1Units={analysis.text?.slp1Units ?? []}
                              onOpenHelp={() => openHelpPdf(null)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {/* Revert and Flag buttons */}
                    <div className="edit-actions">
                      {activeInterface === 'sandhi' && (
                        <>
                          <button
                            className="undo-button"
                            onClick={(e) => handleUndo(index, e)}
                            disabled={!isModified && !isSandhiDone}
                            title="Revert to original sentence"
                          >
                            ↶
                          </button>
                          <button
                            className={`flag-button ${isFlagged ? 'flagged' : ''}`}
                            onClick={(e) => handleFlag(index, e)}
                            title="Flag sentence"
                          >
                            ⚑
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {isMorphOpen && (
                  <Identification
                    key={sentence.xmlid}
                    xmlid={sentence.xmlid}
                    rows={identificationRowsForEdit(
                      sentence.xmlid,
                      sentence.text,
                      completeLexicalByXmlid
                    )}
                    words={splitWords(sentence.text)}
                    onFieldChange={(wi, field, value) =>
                      updateCompleteLexicalField(
                        sentence.xmlid,
                        wi,
                        field,
                        value
                      )
                    }
                    onSave={async ({ xmlid, rows, words }) => {
                      const data = await persistMorphology(xmlid, rows, words);
                      if (
                        String(data?.morph_status ?? '')
                          .trim()
                          .toLowerCase() === 'complete'
                      ) {
                        setMorphSelectedIndex(null);
                      }
                    }}
                  />
                )}
              </div>
            );
              })}
            </div>
          </div>
        </div>
        <div className="app-sidebar-col">
          <div className="sidebar-bar">
            {activeInterface === 'sandhi' ? (
              <SandhiRightSidebar onOpenPdf={openHelpPdf} />
            ) : (
              <IdentificationRightSidebar onOpenPdf={openHelpPdf} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;